// UsersMobile.jsx — mview záložka „Používatelia" (admin only)
// Účel: Mirko cez mobil resetne heslo / PIN remeselníkovi (Mirko 2026-05-26)
// Ústava: 63 (PIN 4 čísla) · 64.2 (admin only)

import React, { useEffect, useState, useMemo } from 'react'
import { COLORS, TYPO, SPACE, RADIUS, hladaj } from '../theme.js'
import { fetchAuth } from '../auth/fetchAuth.js'
import { useToast } from '../components/Toast.jsx'
import { useAuth } from '../auth/AuthContext.jsx'

const ROLE_LABELS = {
  admin:           'Admin',
  owner:           'Majiteľ',
  owner:           'Majiteľ',
  owner_view:      'Majiteľ',
  office_manager:  'Office',
  accountant:      'Back office',   // 24. 9. 2026 premenoval Mirko
  foreman:         'Majster',
  partak:          'Parťák',
  service:         'Servis',
  shop:            'Predajňa',
  driver:          'Šofér',
  omietkar:        'Omietkár',
  poterar:         'Poterár',
  maliar:          'Maliar',
}

const PIN_FORBIDDEN_ROLES = ['omietkar', 'poterar', 'maliar']

export default function UsersMobile() {
  const { user: me } = useAuth()
  const toast = useToast()
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState(null)
  const [search, setSearch] = useState('')
  const [dialog, setDialog] = useState(null) // 'password' | 'pin'

  const nacitaj = () => {
    setLoading(true)
    fetchAuth('/api/auth/users')
      .then(r => r.ok ? r.json() : [])
      .then(d => setUsers(Array.isArray(d) ? d.filter(u => u.active === 1) : []))
      .catch(() => toast.error('Načítanie zlyhalo'))
      .finally(() => setLoading(false))
  }
  useEffect(nacitaj, [])

  const filtered = useMemo(() => {
    if (!search.trim()) return users
    const q = search.toLowerCase()
    return users.filter(u =>
      hladaj((u.username || ''), q) ||
      hladaj((u.display_name || ''), q)
    )
  }, [users, search])

  if (me?.role !== 'admin') {
    return (
      <div style={{ padding: SPACE.xl, textAlign: 'center', color: COLORS.muted }}>
        <div style={{ fontSize: 24, marginBottom: SPACE.sm }}></div>
        <div>Prístup len pre Admin</div>
      </div>
    )
  }

  // Detail bottom-sheet
  if (selected) {
    return (
      <UserDetail
        user={selected}
        onBack={() => { setSelected(null); nacitaj() }}
        onPassword={() => setDialog('password')}
        onPin={() => setDialog('pin')}
        dialog={dialog}
        closeDialog={() => setDialog(null)}
        onSaved={() => { setDialog(null); nacitaj(); }}
      />
    )
  }

  return (
    <div style={{ padding: SPACE.md, paddingBottom: 80 }}>
      <h2 style={{ margin: 0, marginBottom: SPACE.md, fontSize: TYPO.sizes.lg }}>Používatelia</h2>

      <input
        type="text" placeholder="Hľadať..."
        value={search} onChange={e => setSearch(e.target.value)}
        style={{
          width: '100%', padding: '12px 14px', fontSize: TYPO.sizes.base,
          border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.md,
          fontFamily: 'inherit', boxSizing: 'border-box', marginBottom: SPACE.md,
        }}
      />

      {loading ? (
        <div style={{ padding: SPACE.lg, textAlign: 'center', color: COLORS.muted }}>Načítavam…</div>
      ) : filtered.length === 0 ? (
        <div style={{ padding: SPACE.lg, textAlign: 'center', color: COLORS.muted }}>Žiadny používateľ.</div>
      ) : (
        filtered.map(u => (
          <button key={u.id} onClick={() => setSelected(u)} style={{
            display: 'block', width: '100%', textAlign: 'left',
            padding: '14px 16px', marginBottom: 8,
            background: COLORS.bg, border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.md,
            cursor: 'pointer', fontFamily: 'inherit',
          }}>
            <div style={{ fontSize: TYPO.sizes.base, fontWeight: TYPO.weights.semibold }}>
              {u.display_name || u.username}
            </div>
            <div style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, marginTop: 4 }}>
              {ROLE_LABELS[u.role] || u.role} · <span style={{ fontFamily: TYPO.mono }}>{u.username}</span>
            </div>
          </button>
        ))
      )}
    </div>
  )
}

// ─── DETAIL ───────────────────────────────────────────────────
function UserDetail({ user, onBack, onPassword, onPin, dialog, closeDialog, onSaved }) {
  const toast = useToast()
  const cardStyle = {
    background: COLORS.bg, border: `1px solid ${COLORS.line}`,
    borderRadius: RADIUS.md, padding: SPACE.md, marginBottom: SPACE.md,
  }
  const bigBtn = {
    width: '100%', padding: '14px 16px',
    fontSize: TYPO.sizes.base, fontWeight: TYPO.weights.semibold,
    background: COLORS.add, color: '#fff', border: 'none',
    borderRadius: RADIUS.md, cursor: 'pointer', fontFamily: 'inherit', marginBottom: 8,
  }

  return (
    <div style={{ padding: SPACE.md, paddingBottom: 80 }}>
      <button onClick={onBack} style={{
        background: 'transparent', border: 'none', color: COLORS.add,
        fontSize: TYPO.sizes.base, padding: 0, marginBottom: SPACE.md, cursor: 'pointer', fontFamily: 'inherit',
      }}>← Späť na zoznam</button>

      <div style={cardStyle}>
        <div style={{ fontSize: TYPO.sizes.lg, fontWeight: TYPO.weights.bold }}>
          {user.display_name || user.username}
        </div>
        <div style={{ fontSize: TYPO.sizes.sm, color: COLORS.muted, marginTop: 4 }}>
          {ROLE_LABELS[user.role] || user.role} · <span style={{ fontFamily: TYPO.mono }}>{user.username}</span>
        </div>
      </div>

      <button onClick={onPassword} style={bigBtn}>Resetnúť heslo</button>

      {!PIN_FORBIDDEN_ROLES.includes(user.role) && (
        <button onClick={onPin} style={bigBtn}>{user.has_pin ? 'Zmeniť' : 'Prideliť'} PIN (4 čísla)</button>
      )}

      {PIN_FORBIDDEN_ROLES.includes(user.role) && (
        <div style={{ ...cardStyle, fontSize: TYPO.sizes.sm, color: COLORS.muted }}>
          Rola „{ROLE_LABELS[user.role]}" nemá PIN (ústava 63.3).
        </div>
      )}

      <div style={{ ...cardStyle, fontSize: TYPO.sizes.sm, color: COLORS.muted }}>
        <b>Individuálne práva nad rámec roly</b> nastavíš na počítači (Nastavenia → Používatelia).
      </div>

      {dialog === 'password' && <PasswordSheet user={user} onClose={closeDialog} onSaved={onSaved} />}
      {dialog === 'pin' && <PinSheet user={user} onClose={closeDialog} onSaved={onSaved} />}
    </div>
  )
}

// ─── PASSWORD bottom sheet ───────────────────────────────────
function PasswordSheet({ user, onClose, onSaved }) {
  const toast = useToast()
  const [pw, setPw] = useState('')
  const [busy, setBusy] = useState(false)

  const save = async () => {
    if (pw.length < 6) { toast.error('Najmenej 6 znakov'); return }
    if (!window.confirm(`Resetnúť heslo pre "${user.username}"?\nNové heslo: ${pw}\n\nOdovzdaj ho používateľovi.`)) return
    setBusy(true)
    try {
      const r = await fetchAuth(`/api/auth/users/${user.id}`, {
        method: 'PUT',
        body: JSON.stringify({ password: pw }),
      })
      const d = await r.json()
      if (!r.ok) { toast.error(d.error || 'Reset zlyhal'); setBusy(false); return }
      toast.success('✓ Heslo zmenené')
      onSaved?.()
    } catch (e) { toast.error('Chyba: ' + e.message); setBusy(false) }
  }

  return (
    <BottomSheet title="Reset hesla" onClose={onClose}>
      <input
        type="text" value={pw} onChange={e => setPw(e.target.value)}
        placeholder="Nové heslo (≥6 znakov)" autoFocus
        style={sheetInput}
      />
      <button onClick={save} disabled={busy} style={sheetSaveBtn(busy)}>
        {busy ? 'Ukladám…' : 'Uložiť heslo'}
      </button>
    </BottomSheet>
  )
}

// ─── PIN bottom sheet ────────────────────────────────────────
function PinSheet({ user, onClose, onSaved }) {
  const toast = useToast()
  const [pin, setPin] = useState('')
  const [busy, setBusy] = useState(false)

  const save = async () => {
    if (!/^\d{4}$/.test(pin)) { toast.error('PIN = presne 4 čísla'); return }
    setBusy(true)
    try {
      const r = await fetchAuth(`/api/auth/users/${user.id}/action-pin`, {
        method: 'PUT',
        body: JSON.stringify({ pin }),
      })
      const d = await r.json()
      if (!r.ok) { toast.error(d.error || 'PIN zlyhal'); setBusy(false); return }
      toast.success('✓ PIN nastavený')
      onSaved?.()
    } catch (e) { toast.error('Chyba: ' + e.message); setBusy(false) }
  }

  return (
    <BottomSheet title="PIN (4 čísla)" onClose={onClose}>
      <input
        type="text" inputMode="numeric" pattern="\d{4}" maxLength={4}
        value={pin}
        onChange={e => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
        placeholder="0000" autoFocus
        style={{ ...sheetInput, fontFamily: TYPO.mono, letterSpacing: '0.5em', textAlign: 'center', fontSize: 24 }}
      />
      <button onClick={save} disabled={busy} style={sheetSaveBtn(busy)}>
        {busy ? 'Ukladám…' : 'Uložiť PIN'}
      </button>
    </BottomSheet>
  )
}

// ─── BottomSheet wrapper ────────────────────────────────────
function BottomSheet({ title, children, onClose }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 10000,
      display: 'flex', alignItems: 'flex-end',
    }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{
        width: '100%', background: COLORS.bg,
        borderTopLeftRadius: 16, borderTopRightRadius: 16,
        padding: SPACE.lg, paddingBottom: 32, fontFamily: TYPO.family,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: SPACE.md }}>
          <h3 style={{ margin: 0, fontSize: TYPO.sizes.lg }}>{title}</h3>
          <button onClick={onClose} style={{
            background: 'transparent', border: 'none', fontSize: 24, color: COLORS.muted, cursor: 'pointer',
          }}>✕</button>
        </div>
        {children}
      </div>
    </div>
  )
}

const sheetInput = {
  width: '100%', padding: '14px 16px', fontSize: TYPO.sizes.lg,
  border: `2px solid ${COLORS.line}`, borderRadius: RADIUS.md,
  fontFamily: 'inherit', boxSizing: 'border-box', marginBottom: SPACE.md,
}
const sheetSaveBtn = (busy) => ({
  width: '100%', padding: '14px 16px', fontSize: TYPO.sizes.base, fontWeight: TYPO.weights.bold,
  background: COLORS.add, color: '#fff', border: 'none',
  borderRadius: RADIUS.md, cursor: busy ? 'wait' : 'pointer', fontFamily: 'inherit',
  opacity: busy ? 0.5 : 1,
})
